import { makeStyles } from '@material-ui/core';
import BackspaceIcon from '@material-ui/icons/Backspace';
import KeyboardReturnIcon from '@material-ui/icons/KeyboardReturn';
import * as React from 'react';

interface Props {
  onKey: (k: string) => void;
  onBackspace: () => void;
  onEnter: () => void;
  onClear: () => void;
  enterDisabled?: boolean;
}

type Mode = 'letters' | 'numbers';

const LETTER_ROWS: string[][] = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

const NUMBER_ROWS: string[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', ','],
];

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#eceff1',
    padding: theme.spacing(1),
    borderRadius: 8,
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 4,
    '&:last-child': { marginBottom: 0 },
  },
  // The numpad rows are narrower (3 keys) so we center them and keep the
  // keys at the same physical size as letter keys via maxWidth.
  numRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 4,
    '& > button': { maxWidth: 160 },
  },
  key: {
    flex: '1 1 0',
    minHeight: 56,
    background: '#fff',
    border: '1px solid ' + theme.palette.divider,
    borderRadius: 6,
    fontSize: '1.15rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 80ms ease',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    padding: 0,
    '&:active': { background: '#cfd8dc' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  wide: { flex: '2 1 0' },
  extraWide: { flex: '4 1 0' },
  action: { background: '#cfd8dc', fontSize: '0.85rem' },
  modeToggle: {
    background: '#b0bec5',
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  enter: {
    background: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 700,
    '&:active': { background: theme.palette.primary.dark },
  },
}));

export const TabletKeyboard: React.FC<Props> = ({
  onKey,
  onBackspace,
  onEnter,
  onClear,
  enterDisabled,
}) => {
  const classes = useStyles();
  const [mode, setMode] = React.useState<Mode>('letters');

  // Stop touch events from bubbling into MUI Dialog focus-trap, which on
  // some Android browsers re-focuses the search and tries to re-pop the
  // OS keyboard.
  const stop = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const rows = mode === 'numbers' ? NUMBER_ROWS : LETTER_ROWS;
  const rowClass = mode === 'numbers' ? classes.numRow : classes.row;

  return (
    <div
      className={classes.root}
      onTouchStart={stop}
      onMouseDown={(e) => e.preventDefault()}
    >
      {rows.map((row, i) => (
        <div key={i} className={rowClass}>
          {row.map((k) => (
            <button
              key={k}
              type="button"
              className={classes.key}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                stop(e);
                // Letters emit lowercase so the search filter (which
                // toLowerCases the query) matches; digits and
                // punctuation pass through unchanged.
                onKey(mode === 'letters' ? k.toLowerCase() : k);
              }}
            >
              {k}
            </button>
          ))}
        </div>
      ))}
      <div className={classes.row}>
        <button
          type="button"
          aria-label={mode === 'numbers' ? 'Switch to letters' : 'Switch to numbers'}
          className={[classes.key, classes.wide, classes.modeToggle].join(' ')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            stop(e);
            setMode((m) => (m === 'letters' ? 'numbers' : 'letters'));
          }}
        >
          {mode === 'numbers' ? 'ABC' : '123'}
        </button>
        <button
          type="button"
          className={[classes.key, classes.wide, classes.action].join(' ')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            stop(e);
            onClear();
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className={[classes.key, classes.wide].join(' ')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            stop(e);
            onKey(' ');
          }}
        >
          Space
        </button>
        <button
          type="button"
          aria-label="Backspace"
          className={[classes.key, classes.wide, classes.action].join(' ')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            stop(e);
            onBackspace();
          }}
        >
          <BackspaceIcon />
        </button>
        <button
          type="button"
          aria-label="Enter"
          disabled={enterDisabled}
          className={[classes.key, classes.wide, classes.enter].join(' ')}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            stop(e);
            onEnter();
          }}
        >
          <KeyboardReturnIcon />
        </button>
      </div>
    </div>
  );
};
